import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
  Button,
  Chips,
  DataTable,
  Dialog,
  Drawer,
  EmptyState,
  Field,
  Icon,
  Input,
  Panel,
  Select,
  Stat,
  Tag,
  useErrorDialog,
  useToast,
  type Column,
} from '@dayon/design-system';
import { formatCount } from '@dayon/money';
import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { ScreenHeader } from '../shell/ScreenHeader.tsx';
import { openingMonth, shownStatus, statusWords } from './window-words.ts';

/**
 * §18 Maintenance calendar — the planner for every window that takes a surface
 * off the floor.
 *
 * Three routes back this screen:
 *   GET  /api/platform/maintenance             every window, planned_start ascending
 *   POST /api/platform/maintenance             schedules one
 *   POST /api/platform/maintenance/:id/cancel  calls a planned one off
 *
 * A window is a promise made to players: it blocks the affected surface and
 * shows a maintenance screen carrying the end time. So the two facts this
 * screen must never get wrong are WHEN and WHAT IS AFFECTED, and both come
 * straight off platform.maintenance_window.
 *
 * What the two writes deliberately do NOT do is edit. The design draws an
 * editable window; the rule beside it says a window is editable only until it
 * starts, and there is no PATCH route — so a window that needs different times
 * is cancelled and re-scheduled, which leaves both the withdrawal and the
 * replacement in the audit trail rather than one silently overwritten row.
 *
 * The design also draws per-window player-impact copy, a notification CHANNEL
 * mix and an uptime report. None of the three has a source, so each is named
 * under "Not yet available" rather than filled with something plausible — a
 * notification an operator believes went out is worse than a blank.
 */

/* ── Wire shape ─────────────────────────────────────────────────────────────
   snake_case, straight off the SELECT list in services/api/src/modules/
   backoffice/platform.ts. Every integer is text-cast there so a BIGINT never
   arrives as a lossy JS number. */

interface MaintenanceWindow {
  id: string;
  /** MW-208 — the operator-facing reference the design puts in the Window column. */
  ref: string;
  /**
   * A CATEGORY, not a name: PLATFORM · PROVIDER · PAYMENT_RAIL. The human
   * "Evolution Live casino" the design shows under Target is `title`, and the
   * two must not be swapped — `target` is what the overlap rule is scoped by.
   */
  target: string;
  target_id: string | null;
  title: string;
  /** SCHEDULED · COMPLETED in this scope. varchar, not an enum — see the gap note. */
  status: string;
  planned_start: string;
  planned_end: string;
  /** Kept after the fact "because the uptime report needs what ACTUALLY happened". */
  actual_start: string | null;
  actual_end: string | null;
  /** Minutes of notice given to players. 4320 = 72h, 1440 = 24h, 720 = 12h. */
  notice_lead_minutes: string;
  brand: string | null;
  created_by: string | null;
  /** Signed: negative finished early, positive overran. Null until actual_end lands. */
  overrun_minutes: string | null;
}

/* ── Time ───────────────────────────────────────────────────────────────────
   Every window on this screen is read in Philippine time, because that is the
   clock the player-facing maintenance screen counts down in. Formatting in the
   admin's browser locale would put a Manila operator and a window they are
   about to approve on two different days. */

const PHT = 'Asia/Manila';

const dayMonthParts = new Intl.DateTimeFormat('en-PH', { timeZone: PHT, day: '2-digit', month: 'short' });
const clock = new Intl.DateTimeFormat('en-PH', {
  timeZone: PHT,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});
const calendarParts = new Intl.DateTimeFormat('en-PH', {
  timeZone: PHT,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
/** The month heading is a pure calendar label, so it is read off a UTC midnight. */
const monthYear = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', month: 'long', year: 'numeric' });

/** Which PHT calendar day an instant falls on — the calendar grid's only input. */
function phtDayOf(date: Date): { year: number; month: number; day: number } {
  const parts = calendarParts.formatToParts(date);
  const pick = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  return { year: pick('year'), month: pick('month'), day: pick('day') };
}

function phtDay(iso: string): { year: number; month: number; day: number } {
  return phtDayOf(new Date(iso));
}

/**
 * "01 Sep" — assembled from parts, not taken from a locale's own pattern.
 *
 * en-PH orders the month first ("Sep 01"), and every day-first English locale
 * abbreviates September to "Sept". The design writes "01 Sep", and on a screen
 * where "01 Sep" and "09 Jan" must never be confusable the ORDER is the part
 * that carries the meaning, so it is fixed here rather than left to ICU.
 */
function dayMonthLabel(date: Date): string {
  const parts = dayMonthParts.formatToParts(date);
  const pick = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${pick('day')} ${pick('month')}`;
}


/**
 * Has the window begun?
 *
 * The one predicate the immutability rule turns on — a window is editable until
 * it starts and frozen after — so it is written once and read everywhere,
 * rather than re-derived per call site where one comparison could drift.
 */
function hasStarted(w: MaintenanceWindow): boolean {
  return new Date(w.planned_start).getTime() <= Date.now();
}

/* ── Calendar arithmetic ────────────────────────────────────────────────────
   Pure calendar questions — which weekday the 1st is, how many days the month
   has — carry no time zone, so they are answered off a UTC midnight built from
   an ISO string. The month index is allowed to over- or underflow, which is
   what makes the previous/next buttons a single expression. */

function monthStart(year: number, month: number): Date {
  const index = year * 12 + (month - 1);
  const y = Math.floor(index / 12);
  const m = (index % 12) + 1;
  return new Date(`${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-01T00:00:00Z`);
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 29 : 28;
  return [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1] ?? 30;
}

/** "01 Sep · 03:00 – 05:00 PHT", and both dates when a window crosses midnight. */
function whenLabel(w: MaintenanceWindow): string {
  const start = new Date(w.planned_start);
  const end = new Date(w.planned_end);
  const a = phtDay(w.planned_start);
  const b = phtDay(w.planned_end);
  const sameDay = a.year === b.year && a.month === b.month && a.day === b.day;
  return sameDay
    ? `${dayMonthLabel(start)} · ${clock.format(start)} – ${clock.format(end)} PHT`
    : `${dayMonthLabel(start)} ${clock.format(start)} – ${dayMonthLabel(end)} ${clock.format(end)} PHT`;
}

function stamp(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return `${dayMonthLabel(date)} ${phtDayOf(date).year} · ${clock.format(date)} PHT`;
}

function durationLabel(fromIso: string, toIso: string): string {
  const minutes = Math.round((new Date(toIso).getTime() - new Date(fromIso).getTime()) / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

/** "72h before" · "24h before". The stored lead, never a channel list. */
function leadLabel(minutes: string): string {
  const m = Number(minutes);
  if (!Number.isFinite(m) || m <= 0) return 'no lead recorded';
  if (m < 60) return `${m}m before`;
  return `${Math.round(m / 60)}h before`;
}

/**
 * The history modal's Result column, derived rather than stored.
 *
 * Planned vs actual is the whole point of keeping both, so "Completed early"
 * and "Overran 70m" are arithmetic on two real timestamps — nothing here is a
 * judgement the schema does not support.
 */
function resultLabel(w: MaintenanceWindow): string {
  if (w.overrun_minutes === null) return 'no actual end recorded';
  const n = Number(w.overrun_minutes);
  if (n < 0) return `Completed ${Math.abs(n)}m early`;
  if (n === 0) return 'Completed on plan';
  return `Overran ${n}m`;
}

function plannedRange(w: MaintenanceWindow): string {
  return `${dayMonthLabel(new Date(w.planned_start))} · ${clock.format(new Date(w.planned_start))}–${clock.format(new Date(w.planned_end))}`;
}

function actualRange(w: MaintenanceWindow): string {
  if (!w.actual_start && !w.actual_end) return '—';
  const from = w.actual_start ? clock.format(new Date(w.actual_start)) : '?';
  const to = w.actual_end ? clock.format(new Date(w.actual_end)) : 'still running';
  return `${from}–${to}`;
}

/* A window's status in words, and the status it is shown under (a scheduled window whose end has
   passed is past and not marked complete): window-words.ts. */

/** What `target` actually names: the surface a window takes down. */
const TARGET_LABEL: Record<string, string> = {
  PLATFORM: 'Platform',
  PROVIDER: 'Game provider',
  PAYMENT_RAIL: 'Payment rail',
  BRAND: 'Brand',
};

/** What the write routes cover, and where they stop. Repeated verbatim. */
const WRITE_SCOPE =
  'Scheduling and cancelling both work here. Amending does not: a window that needs different times is cancelled and scheduled again.';

/* ── Composing a window ─────────────────────────────────────────────────────
   Every option here is a closed set the contract validates, so a value this
   form can produce is a value the route accepts. */

const TARGET_OPTIONS = [
  { value: 'PLATFORM', label: 'Platform' },
  { value: 'PROVIDER', label: 'Game provider' },
  { value: 'PAYMENT_RAIL', label: 'Payment rail' },
  { value: 'BRAND', label: 'Brand' },
] as const;

/** The three leads the design names, in minutes. 72h · 24h · 12h. */
const LEAD_OPTIONS = [
  { value: 4320, label: '72 hours before — full-downtime release' },
  { value: 1440, label: '24 hours before — partial window' },
  { value: 720, label: '12 hours before — short rail outage' },
] as const;

const CANCEL_REASONS = [
  { code: 'NO_LONGER_REQUIRED', label: 'No longer required' },
  { code: 'RESCHEDULED', label: 'Being rescheduled' },
  { code: 'CLASHES_WITH_ANOTHER_CHANGE', label: 'Clashes with another change' },
  { code: 'VENDOR_DEFERRED', label: 'Vendor deferred it' },
  { code: 'NOTICE_TOO_SHORT', label: 'Notice given was too short' },
] as const;

/**
 * Asia/Manila is UTC+08:00 all year — the Philippines has kept no daylight
 * saving since 1978 — so a wall clock typed into this form converts with one
 * fixed offset. The alternative, letting `datetime-local` be read in the
 * BROWSER's zone, would silently move a window by hours for an operator
 * travelling or on a laptop set to UTC, and the field is labelled PHT.
 */
const PHT_OFFSET = '+08:00';

/** "2026-09-18T02:00" as typed in Manila → the instant, in ISO 8601. */
function phtToIso(local: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(local)) return null;
  const at = new Date(`${local}:00.000${PHT_OFFSET}`);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

/** The reverse, for a default that lands on a readable Manila hour. */
function isoToPht(iso: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: PHT,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(iso));
  const pick = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${pick('year')}-${pick('month')}-${pick('day')}T${pick('hour')}:${pick('minute')}`;
}

/** A window can be called off only while it is still ahead and still open. */
function isCancellable(w: MaintenanceWindow): boolean {
  return !hasStarted(w) && w.status !== 'CANCELLED' && w.status !== 'COMPLETED';
}

/* ── Calendar chrome ────────────────────────────────────────────────────────
   The month grid is a real <table>: a calendar IS tabular, and building it from
   <div>s would have cost a caption, column headers and the row/column
   relationship that a screen reader uses to say "Tuesday 1". */

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

const calendarTable: CSSProperties = {
  width: '100%',
  tableLayout: 'fixed',
  borderCollapse: 'separate',
  borderSpacing: 'var(--space-1)',
  // Seven readable columns need room; below this the container scrolls, never
  // the page — a squeezed calendar cell hides the label that makes it useful.
  minWidth: '560px',
};

const calendarHead: CSSProperties = {
  textAlign: 'left',
  fontSize: '11px',
  fontWeight: 500,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--color-text-subtle)',
  padding: 'var(--space-1)',
};

const baseCell: CSSProperties = {
  verticalAlign: 'top',
  height: '62px',
  padding: 'var(--space-2)',
  borderRadius: 'var(--radius-sm)',
  fontSize: '11.5px',
};

const dayCell: CSSProperties = { ...baseCell, background: 'var(--color-bg)', color: 'var(--color-text)' };
/** Weekends read dimmer, as the design has them — a change lands on them less often. */
const weekendCell: CSSProperties = { ...baseCell, background: 'var(--color-bg)', color: 'var(--color-text-subtle)' };
const markedCell: CSSProperties = {
  ...baseCell,
  background: 'var(--color-tag-accent-bg)',
  color: 'var(--color-tag-accent-fg)',
  // The accent inset border, so a marked day is not carried by fill alone.
  boxShadow: 'inset 0 0 0 1px var(--portal-accent)',
};
const padCell: CSSProperties = { ...baseCell, background: 'transparent' };
const dayNumber: CSSProperties = { fontSize: '12.5px', fontVariantNumeric: 'tabular-nums' };
const markText: CSSProperties = { marginTop: 'var(--space-1)', overflowWrap: 'anywhere' };
const subLine: CSSProperties = { fontSize: '11px' };

/* ── Columns ────────────────────────────────────────────────────────────────
   A factory, because the Action cell has to reach component state: the design
   opens a window from its own row button rather than from the whole row, and
   the row carries no second target that a stray click could hit. */

function windowColumns(onOpen: (row: MaintenanceWindow) => void): Column<MaintenanceWindow>[] {
  return [
    {
      key: 'ref',
      header: 'Window',
      render: (w) => <Tag tone={w.status === 'COMPLETED' ? 'quiet' : 'accent'}>{w.ref}</Tag>,
    },
    {
      key: 'target',
      header: 'Target',
      render: (w) => (
        <>
          {w.title}
          <div className="text-subtle" style={subLine}>
            {TARGET_LABEL[w.target] ?? w.target} · {w.brand ?? 'all brands'}
          </div>
        </>
      ),
    },
    {
      key: 'when',
      header: 'When',
      render: (w) => (
        <>
          <span className="tabular">{whenLabel(w)}</span>
          <div className="text-subtle" style={subLine}>
            {durationLabel(w.planned_start, w.planned_end)} planned
          </div>
        </>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (w) => {
        const chip = statusWords(shownStatus(w));
        return <Tag tone={chip.tone}>{chip.label}</Tag>;
      },
    },
    {
      key: 'impact',
      header: 'Player impact',
      secondary: true,
      // Empty on every row, and deliberately so: maintenance_window has no
      // impact column. Writing "Live tables unavailable" here would be this
      // screen inventing the sentence a player is shown.
      render: () => <span className="text-subtle">not stored</span>,
    },
    {
      key: 'notify',
      header: 'Notification',
      secondary: true,
      // The LEAD is real (notice_lead_minutes); the channel mix is not stored.
      render: (w) => <span className="tabular">{leadLabel(w.notice_lead_minutes)}</span>,
    },
    {
      key: 'action',
      header: 'Action',
      numeric: true,
      render: (w) => (
        <Button small onClick={() => onOpen(w)}>
          Open
        </Button>
      ),
    },
  ];
}

/** LISTS.maintHistory — planned against actual, which is what an uptime report is. */
const historyColumns: Column<MaintenanceWindow>[] = [
  {
    key: 'ref',
    header: 'Window',
    render: (w) => <Tag tone="quiet">{w.ref}</Tag>,
  },
  {
    key: 'target',
    header: 'Target',
    render: (w) => (
      <>
        {w.title}
        <div className="text-subtle" style={subLine}>
          {TARGET_LABEL[w.target] ?? w.target}
        </div>
      </>
    ),
  },
  {
    key: 'planned',
    header: 'Planned',
    render: (w) => <span className="tabular">{plannedRange(w)}</span>,
  },
  {
    key: 'actual',
    header: 'Actual',
    render: (w) => <span className="tabular">{actualRange(w)}</span>,
  },
  {
    key: 'result',
    header: 'Result',
    render: (w) => {
      const overrun = w.overrun_minutes === null ? null : Number(w.overrun_minutes);
      return (
        <Tag tone={overrun !== null && overrun > 0 ? 'outline' : 'neutral'}>{resultLabel(w)}</Tag>
      );
    },
  },
];

export function Maintenance() {
  const { allowed, brandId, brands } = useSession();
  /**
   * The capability this module needs, read once and used in BOTH places that
   * need it: the refusal that decides what is painted, and the effects that
   * decide what is fetched. An early return covers only the first.
   */
  const canManageAccess = allowed('access.manage');
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  const [rows, setRows] = useState<MaintenanceWindow[]>([]);
  const [loading, setLoading] = useState(true);
  /*
   * Whether the read FAILED, as distinct from returning nothing.
   *
   * One request backs every surface here, and it had two states — loading and
   * done. A rejected read therefore left an unmarked month grid, tiles at zero
   * and a table saying "This is a real zero — the list is read live off
   * platform.maintenance_window". An operator who dismisses the error modal
   * then believes the floor is clear, which is the belief that puts a release
   * into a live table. "Nothing is scheduled" and "we could not ask" are
   * different facts, and only the first is safe to plan against.
   */
  const [loadFailed, setLoadFailed] = useState(false);
  const [filter, setFilter] = useState<string>('ALL');
  /** Months away from the anchor month, so the calendar can be walked either way. */
  const [monthOffset, setMonthOffset] = useState(0);
  const [detail, setDetail] = useState<MaintenanceWindow | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  /* ── Writing ──────────────────────────────────────────────────────────────
     `writeCount` is bumped after every accepted write so the list re-reads.
     Patching the changed row in place would leave the month grid, the four
     tiles and the status chips describing the calendar as it was a moment ago —
     and those counters are what a release is planned against. */
  const [writeCount, setWriteCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [form, setForm] = useState({
    title: '',
    target: 'PLATFORM' as (typeof TARGET_OPTIONS)[number]['value'],
    targetId: '',
    brandId: '',
    start: '',
    end: '',
    lead: 4320,
  });
  /** Per-field messages, rendered inline. Route failures open the dialog. */
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [cancelling, setCancelling] = useState<MaintenanceWindow | null>(null);
  const [cancelReason, setCancelReason] = useState<string>(CANCEL_REASONS[0].code);
  const [cancelNote, setCancelNote] = useState('');

  useEffect(() => {
    /*
     * NOT ASKED, rather than asked and refused. The module's refusal below is a
     * RENDER decision and runs after every hook here was registered, so this read
     * still went out for a role the route refuses -- and the 403 raised a modal
     * claiming a failure on top of the notice explaining the refusal.
     */
    if (!canManageAccess) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (brandId) params.set('brandId', brandId);
    const query = params.toString();

    api
      .get<{ items: MaintenanceWindow[] }>(
        `/api/platform/maintenance${query ? `?${query}` : ''}`,
      )
      .then((d) => {
        if (cancelled) return;
        setRows(d.items);
        setLoadFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Cleared alongside the flag: windows read under the previous brand
        // scope sitting beneath a failure notice would read as this scope's.
        setRows([]);
        setLoadFailed(true);
        showError({
          title: 'Could not load the maintenance calendar',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        });
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [canManageAccess, brandId, showError, writeCount]);

  /**
   * Open the composer on a sane default: tomorrow, 02:00–04:00 Manila.
   *
   * The design's own example window. It also keeps the form clear of the two
   * refusals a blank form walks straight into — a start in the past, and a
   * notice period that cannot fit before it.
   */
  const openScheduler = useCallback(() => {
    const base = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000);
    const day = isoToPht(base.toISOString()).slice(0, 10);
    setForm({
      title: '',
      target: 'PLATFORM',
      targetId: '',
      brandId: brandId ?? '',
      start: `${day}T02:00`,
      end: `${day}T04:00`,
      lead: 4320,
    });
    setFormErrors({});
    setScheduleOpen(true);
  }, [brandId]);

  /**
   * Validate what this form can know, before anything is sent.
   *
   * The route re-checks every one of these — a browser is not a guard — but a
   * field problem belongs beside the field, not in a modal the operator has to
   * dismiss before they can see which box was wrong.
   */
  const validate = useCallback((): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (form.title.trim().length < 3) {
      errors['title'] = 'Name the surface as a player would recognise it, at least 3 characters.';
    }
    const startIso = phtToIso(form.start);
    const endIso = phtToIso(form.end);
    if (!startIso) errors['start'] = 'Give a start date and time.';
    if (!endIso) errors['end'] = 'Give an end date and time.';
    if (startIso && endIso && new Date(endIso) <= new Date(startIso)) {
      errors['end'] = 'The end must be later than the start.';
    }
    if (startIso && new Date(startIso).getTime() <= Date.now()) {
      errors['start'] = 'A window is immutable once it starts, so it cannot be scheduled behind us.';
    }
    if (startIso && new Date(startIso).getTime() - Date.now() < form.lead * 60_000) {
      errors['lead'] =
        `This window starts sooner than ${Math.round(form.lead / 60)} hours from now, so that much notice cannot have been given.`;
    }
    if (form.target === 'BRAND' && !form.brandId) {
      errors['brandId'] = 'A brand window must name the brand it takes down.';
    }
    return errors;
  }, [form]);

  const submitSchedule = useCallback(async () => {
    const errors = validate();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    try {
      const created = await api.post<{ ref: string }>('/api/platform/maintenance', {
        target: form.target,
        ...(form.targetId.trim() ? { targetId: form.targetId.trim() } : {}),
        ...(form.brandId ? { brandId: form.brandId } : {}),
        title: form.title.trim(),
        plannedStart: phtToIso(form.start),
        plannedEnd: phtToIso(form.end),
        noticeLeadMinutes: form.lead,
      });
      toast(`${created.ref} scheduled — ${form.title.trim()} is on the calendar.`);
      setScheduleOpen(false);
      setWriteCount((n) => n + 1);
    } catch (error: unknown) {
      // A refusal the route computed and this form could not: an overlap with a
      // window in another brand's scope, or a brand outside this admin's.
      if (error instanceof ApiRequestError && error.fields) setFormErrors(error.fields);
      showError({
        title: 'The window was not scheduled',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was saved. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setSaving(false);
    }
  }, [form, validate, showError, toast]);

  const submitCancel = useCallback(async () => {
    if (!cancelling) return;
    setSaving(true);
    try {
      await api.post(`/api/platform/maintenance/${cancelling.id}/cancel`, {
        reasonCode: cancelReason,
        note: cancelNote.trim(),
      });
      toast(`${cancelling.ref} cancelled. The reason is on the audit trail.`);
      setCancelling(null);
      setCancelNote('');
      // The drawer showed the window as it was before the decision.
      setDetail(null);
      setWriteCount((n) => n + 1);
    } catch (error: unknown) {
      showError({
        title: `Could not cancel ${cancelling.ref}`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'The window is unchanged. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setSaving(false);
    }
  }, [cancelling, cancelReason, cancelNote, showError, toast]);

  /**
   * Which month the calendar opens on: the month a Manila operator is standing
   * in. It opened on the next window not yet started, or else the last one
   * read, so on 30 October it opened on September with every window there
   * already past (module walk, 30 Oct 2026). The arrows walk from today.
   */
  const anchor = useMemo(() => openingMonth(new Date()), []);

  const view = useMemo(() => {
    const index = anchor.year * 12 + (anchor.month - 1) + monthOffset;
    return { year: Math.floor(index / 12), month: (index % 12) + 1 };
  }, [anchor, monthOffset]);

  const monthLabel = monthYear.format(monthStart(view.year, view.month));
  const previousLabel = monthYear.format(monthStart(view.year, view.month - 1));
  const nextLabel = monthYear.format(monthStart(view.year, view.month + 1));

  /**
   * The month grid.
   *
   * Mon→Sun, so the first cell of the month is offset by its weekday: 1 Sep
   * 2026 is a Tuesday and therefore starts in column 2. Computed from a UTC
   * calendar date, which is a pure day-of-week question and carries no zone.
   */
  const calendar = useMemo(() => {
    const leading = (monthStart(view.year, view.month).getUTCDay() + 6) % 7;
    const dayCount = daysInMonth(view.year, view.month);

    const marks = new Map<number, MaintenanceWindow[]>();
    for (const w of rows) {
      const d = phtDay(w.planned_start);
      if (d.year !== view.year || d.month !== view.month) continue;
      marks.set(d.day, [...(marks.get(d.day) ?? []), w]);
    }

    type Cell = { day: number; weekend: boolean; windows: MaintenanceWindow[] } | null;
    const cells: Cell[] = Array.from({ length: leading }, () => null);
    for (let day = 1; day <= dayCount; day++) {
      const column = (leading + day - 1) % 7;
      cells.push({ day, weekend: column >= 5, windows: marks.get(day) ?? [] });
    }
    while (cells.length % 7 !== 0) cells.push(null);

    const weeks: Cell[][] = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

    return { weeks, inMonth: [...marks.values()].flat().length, markedDays: marks.size };
  }, [rows, view]);

  /** Mutually exclusive status chips, built from the statuses actually present. */
  const chipOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const w of rows) counts.set(shownStatus(w), (counts.get(shownStatus(w)) ?? 0) + 1);
    // A badge is a count, and a count is a claim about what was read. After a
    // failed read the row said "All 0" directly above a table explaining that
    // nothing came back — so the chips carry no number until there is one.
    if (loadFailed) return [{ key: 'ALL', label: 'All' }];
    return [
      { key: 'ALL', label: 'All', count: rows.length },
      ...[...counts.entries()].map(([key, count]) => ({
        key,
        label: statusWords(key).label,
        count,
      })),
    ];
  }, [rows, loadFailed]);

  const visible = useMemo(
    () => (filter === 'ALL' ? rows : rows.filter((w) => shownStatus(w) === filter)),
    [rows, filter],
  );

  /* The statuses actually STORED in this scope, in the order the rows first carry them. Not the
     chips: a chip can be "Past, not marked complete", which is shown and never stored. */
  const storedStatuses = useMemo(() => [...new Set(rows.map((w) => w.status))], [rows]);

  // setDetail is stable, so the column array is built once rather than on every
  // keystroke elsewhere on the page.
  const columns = useMemo(() => windowColumns(setDetail), []);

  /**
   * History is the same read, narrowed to windows that actually ran. Newest
   * first — a completed window is looked up backwards from the incident.
   */
  const history = useMemo(
    () =>
      rows
        .filter((w) => w.actual_start !== null || w.actual_end !== null)
        .toSorted((a, b) => b.planned_start.localeCompare(a.planned_start)),
    [rows],
  );

  const figures = useMemo(() => {
    const upcoming = rows.filter((w) => !hasStarted(w));
    const overruns = history
      .map((w) => (w.overrun_minutes === null ? null : Number(w.overrun_minutes)))
      .filter((n): n is number => n !== null && Number.isFinite(n));
    return {
      upcoming,
      next: upcoming[0] ?? null,
      worstOverrun: overruns.length > 0 ? Math.max(...overruns) : null,
    };
  }, [rows, history]);

  // Every route in the Platform group sits behind access.manage, so a role
  // without it is told which capability it lacks rather than shown a dialog
  // it can do nothing about.
  if (!canManageAccess) {
    return (
      <EmptyState icon="lock-key" title="Maintenance calendar">
        This module needs the access.manage capability. Your role does not hold it.
      </EmptyState>
    );
  }

  return (
    <>
      <ScreenHeader
        /*
          THE HEADING KEEPS "CALENDAR", THOUGH THE SIDEBAR SAYS ONLY "MAINTENANCE".

          The sidebar entry is one word, and the h3 this replaces was two,
          because the month grid IS the screen: an operator arriving from a
          release plan is looking for a date, and the thing they have to read
          first is a calendar, not a list. Narrowing the heading to match the
          sidebar exactly would drop the one word that says what they are
          looking at, and the broader name still contains the sidebar's own
          word, so somebody who clicked "Maintenance" cannot doubt they
          arrived. The capability refusal above is titled the same way for the
          same reason — one screen, one name, whichever path reaches it.
        */
        title="Maintenance calendar"
        purpose="The planner for downtime: whoever is taking games, payments or the whole site offline books the window here, and calls it off if plans change."
        /*
          The caption kept exactly as it was, all three states and the month.

          It is tri-state and the three states are NOT interchangeable.
          "Loading…" means the read has not come back yet; "the calendar could
          not be read" means it failed, so nothing below counted anything at
          all; and the counts mean the platform genuinely answered. Collapsing
          that middle state into a zero would turn a failed read into the claim
          that the month is clear — which, on the one screen a release is
          scheduled against, is the most expensive sentence here. It is the same
          distinction the four tiles, the undrawn month grid and the table's
          empty state each go out of their way to keep.

          `monthLabel` leads all three states on purpose, including the failed
          one: the previous and next buttons move the month, and a reader who
          does not know WHICH month failed to read cannot judge the failure any
          more than they could judge a count.
        */
        meta={
          loading
            ? 'Loading…'
            : loadFailed
              ? `${monthLabel} · the calendar could not be read — nothing below counts windows`
              : `${monthLabel} · ${calendar.inMonth === 1 ? '1 window' : `${formatCount(calendar.inMonth)} windows`} planned · ${formatCount(rows.length)} read in this scope`
        }
        actions={
          <>
            <Button onClick={() => setHistoryOpen(true)}>
              <Icon name="clock-counter-clockwise" />
              History
            </Button>
            {/* Live. The toast it raises claims one thing only — that the window
                is on the calendar — because that is the one thing the write does.
                Nothing here notifies a player; see the notification note below. */}
            <Button variant="primary" onClick={openScheduler}>
              <Icon name="plus" />
              Schedule window
            </Button>
          </>
        }
      />

      <div className="notice notice-quiet">
        <Icon name="info" />
        <div>
          A window blocks the affected surface and shows the player a maintenance screen carrying
          the end time. Full-downtime releases need 72 hours notice; partial windows need 24.
          Overlapping windows on the same target are rejected at save, and a window — its scope and
          its notification plan — is editable only until it starts. After that it is immutable.
        </div>
      </div>

      {/* ── Headline figures, all four counted off the rows on this page. ── */}
      {loading && rows.length === 0 ? (
        <div className="stat-grid">
          {Array.from({ length: 4 }, (_, i) => (
            <div className="stat" key={i}>
              <div className="skeleton" style={{ height: '11px', width: '60%' }}>
                &nbsp;
              </div>
              <div className="skeleton" style={{ height: '25px', width: '80%' }}>
                &nbsp;
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="stat-grid">
          {/* An em dash wherever the read failed, never a zero. "Windows ahead
              0" is a figure a release is scheduled against, and a failed read
              counted nothing at all. */}
          <Stat
            label="Windows ahead"
            value={loadFailed ? '—' : formatCount(figures.upcoming.length)}
            delta={
              loadFailed
                ? 'the read failed — no window came back to count'
                : `of ${formatCount(rows.length)} read${brandId ? ' · brand-scoped' : ' · all brands'}`
            }
          />
          <Stat
            label="Next window"
            value={loadFailed ? '—' : figures.next ? figures.next.ref : '—'}
            delta={
              loadFailed
                ? 'nothing was read, so nothing here is known to be scheduled'
                : figures.next
                  ? `${figures.next.title} · ${whenLabel(figures.next)}`
                  : 'nothing scheduled ahead of now'
            }
          />
          <Stat
            label={`In ${monthLabel}`}
            value={loadFailed ? '—' : formatCount(calendar.inMonth)}
            delta={
              loadFailed
                ? 'no window was read, for this month or any other'
                : `across ${calendar.markedDays === 1 ? '1 day' : `${formatCount(calendar.markedDays)} days`}`
            }
          />
          <Stat
            label="Windows that ran"
            value={loadFailed ? '—' : formatCount(history.length)}
            delta={
              loadFailed
                ? 'history is this same read, and it failed too'
                : figures.worstOverrun === null
                  ? 'no actual times recorded yet'
                  : figures.worstOverrun > 0
                    ? `worst overran ${figures.worstOverrun}m`
                    : 'all finished on or before plan'
            }
          />
        </div>
      )}

      {/* ── The month grid ─────────────────────────────────────────────────── */}
      <Panel
        title={monthLabel}
        actions={
          <div className="row">
            <Button
              small
              iconOnly
              aria-label={`Previous month, ${previousLabel}`}
              onClick={() => setMonthOffset((o) => o - 1)}
            >
              <Icon name="caret-left" />
            </Button>
            <Button
              small
              iconOnly
              aria-label={`Next month, ${nextLabel}`}
              onClick={() => setMonthOffset((o) => o + 1)}
            >
              <Icon name="caret-right" />
            </Button>
          </div>
        }
      >
        {loading && rows.length === 0 ? (
          <div className="skeleton" style={{ height: '300px' }}>
            &nbsp;
          </div>
        ) : loadFailed ? (
          /* The grid is not drawn at all. Every unmarked day in it would be a
             claim that the day is clear, and this read established nothing. */
          <div className="notice notice-quiet">
            <Icon name="warning-circle" />
            {/* The dangling "The request to …" left here by an earlier sweep — it
                had named the endpoint, and removing the name left half a
                sentence — is written out properly rather than patched: what an
                operator needs from this notice is that NOTHING was established,
                and a broken sentence is the one shape that makes a reader doubt
                the rest of the screen instead of the read. */}
            <div>
              <strong>The calendar could not be read.</strong> The request for this month&rsquo;s
              windows did not come back, so no month is drawn: an unmarked grid would say every
              day of {monthLabel} is clear, and nothing here knows that. Reload to try the read
              again.
            </div>
          </div>
        ) : (
          <div className="table-scroll">
            <table style={calendarTable}>
              <caption className="sr-only">
                {monthLabel} maintenance calendar, Monday to Sunday. A marked day carries at least
                one planned window, named with its Philippine start time.
              </caption>
              <thead>
                <tr>
                  {WEEKDAYS.map((d) => (
                    <th key={d} scope="col" style={calendarHead}>
                      {d}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {calendar.weeks.map((week, w) => (
                  <tr key={w}>
                    {week.map((cell, c) =>
                      cell === null ? (
                        <td key={c} style={padCell} aria-hidden="true" />
                      ) : (
                        <td
                          key={c}
                          style={
                            cell.windows.length > 0
                              ? markedCell
                              : cell.weekend
                                ? weekendCell
                                : dayCell
                          }
                        >
                          <div style={dayNumber}>{cell.day}</div>
                          {cell.windows.map((w2) => (
                            // The mark names the window and its start time, never
                            // the accent fill alone — colour is not a label.
                            <div key={w2.id} style={markText}>
                              {w2.title} {clock.format(new Date(w2.planned_start))}
                            </div>
                          ))}
                        </td>
                      ),
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* ── The windows themselves ─────────────────────────────────────────── */}
      <div className="row">
        <Chips
          options={chipOptions}
          active={filter}
          onChange={setFilter}
          label="Filter maintenance windows by status"
        />
      </div>

      <Panel title="Maintenance windows">
        <div className="notice notice-quiet" style={{ marginBottom: 'var(--space-3)' }}>
          <Icon name="info" />
          <div>
            Open shows a window in full and offers <strong>Cancel window</strong> while it is still
            ahead. {WRITE_SCOPE} <strong>Player impact</strong> is blank on every row because
            nothing records it, and <strong>Notification</strong> shows the stored lead
            time only — a channel mix such as “In-app + email + push” is not stored
            anywhere, so scheduling a window records <em>when</em> players are due to be told and
            never <em>how</em>, and sends nothing. Platform-wide windows carry no brand and are
            returned in every brand scope, so selecting a brand adds that brand's own windows rather
            than hiding these.
          </div>
        </div>

        {loading && rows.length === 0 ? (
          <div className="skeleton" style={{ height: '240px' }}>
            &nbsp;
          </div>
        ) : (
          <DataTable
            caption="Maintenance windows in planned order, with target, timing, status and notice lead"
            columns={columns}
            rows={visible}
            rowKey={(w) => w.id}
            empty={
              /* THE TWO EMPTIES ARE DIFFERENT FACTS AND STAY DIFFERENT SENTENCES.
                 `loadFailed` means nothing came back, so nothing is known; the
                 other branch means the platform answered and the answer was
                 none. On a screen somebody checks before a release, "no window
                 is scheduled" and "we could not find out" are opposite
                 instructions, and only the first one is safe to act on.

                 The real-zero sentence used to say the list is "read live off
                 platform.maintenance_window, not cached". The reassurance it
                 was making is the one worth keeping — that nothing is being
                 hidden by a stale copy or a filter — so it is now made in words
                 an operator can act on. The table behind it is
                 `platform.maintenance_window`, re-read on every mount and after
                 every accepted write (see `writeCount`), with no cache in
                 front of it. */
              <EmptyState
                icon="calendar-blank"
                title={loadFailed ? 'The window list could not be read' : 'No window matches'}
              >
                {loadFailed
                  ? 'The read failed, so this table is empty because nothing came back — not because no window is scheduled. There may be a window running or about to start; this screen cannot say. Reload to try the read again.'
                  : filter === 'ALL'
                    ? 'No maintenance window is recorded in this brand scope. This is a real zero, not a filter and not a stale copy: the list is fetched fresh every time you open the screen, and it came back with nothing in it.'
                    : `No window is ${statusWords(filter).label.toLowerCase()} in this brand scope. Choose All to see every window read.`}
              </EmptyState>
            }
          />
        )}
      </Panel>

      {/* ── What the design shows and the platform cannot yet back ─────────── */}
      <Panel title="Not yet available">
        <div className="stack">
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              {/* Only POST /api/platform/maintenance and .../:id/cancel exist; no PATCH. */}
              <strong>Editing an open window</strong> — scheduling and cancelling work; changing a
              window does not. There is no way to amend one, and the rule above says a window is
              editable only until it starts, so the honest shape of a change is to cancel the window
              and schedule a replacement: the audit trail then carries the withdrawal and the new
              window, rather than one entry that quietly became something else. Players are not
              notified when a window is scheduled, so the message shown after scheduling says only
              that the window is on the calendar.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            {/* `platform.maintenance_window` holds `target`, `target_id`, `title`
                and the four timestamps, and nothing resembling impact prose. The
                note below says that in operator words because the operator's
                question is not which columns exist — it is why the Player impact
                column is blank on every row, and whether that blank means
                "no impact" (it does not) or "nowhere to record one" (it does). */}
            <div>
              <strong>Player impact</strong> — a window records what it takes down, what it is
              called and when it runs. There is nowhere to keep the sentence a player is shown,
              so “Live tables unavailable” and “38 slot titles unavailable” cannot be stored
              against a window and the column stays blank on every row — that blank means the
              wording was never recorded, not that nobody is affected. The Target column names
              the affected surface, which is the closest thing actually kept.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            {/* The stored facts, for whoever reads the source: `notice_lead_minutes`
                is an integer of minutes and is the whole of it. There is no
                `notice_channels` column, and no foreign key from
                `platform.maintenance_window` to the `platform.announcement` a
                window ought to trigger — the two tables are unrelated today, which
                is why scheduling a window sends nothing. The note below keeps the
                warning and drops the names, because the thing an operator must not
                get wrong is believing a notice went out. */}
            <div>
              <strong>Notification channels</strong> — all that is kept against a window is how
              much notice was promised, as a number of minutes. Nothing records which channels
              the notice went out on, and a window is not tied to the announcement it ought to
              trigger, so this screen can say <em>when</em> players were due to be told and
              never <em>how</em>. Scheduling a window here sends nothing to anybody.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            {/* THE JARGON IS GONE FROM THE SCREEN AND KEPT IN THE TEST.
                The guarded records are maintenance windows; the rule is a
                trigger, `trg_maintenance_overlap`, declared in
                sql/040_business_rules.sql, and the database refuses the write
                with a check_violation, SQLSTATE 23514.

                None of that belongs here. An operator who has just been refused
                a window cannot look up a trigger, and cannot do anything with an
                SQLSTATE — the two things they CAN use are that the refusal came
                from the database rather than from this screen being careful, and
                that a window starting exactly when another ends is allowed.

                The names could not simply be deleted, because the end-to-end
                test "the overlap rule the screen describes is really enforced by
                the database" asserted them as VISIBLE TEXT. That test is a good
                one and its shape is kept: it reads the claim off the screen and
                then proves it against a real database, so it fails if the screen
                stops claiming it as well as if the database stops holding it.
                What changed is WHICH words carry the link. The spec now asserts
                the operator sentence on screen and keeps `23514` in its own
                assertion against the database, where a developer reads it. */}
            <div>
              <strong>Overlap rejection</strong> — now reachable, and enforced twice. Two
              windows on the same target cannot overlap: the screen refuses it first, so the
              message can name the window you clashed with, and{' '}
              <strong>the platform refuses it again</strong> underneath — so a window written by
              anything that bypasses this screen is rejected too. That second refusal is the one
              worth knowing about, because it means the rule holds even when this screen is not
              the thing doing the writing. The ranges are half-open, so a window that starts
              exactly when another ends is accepted rather than rejected. Cancelling a window
              releases its slot, because a cancelled window is excluded from the check.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              <strong>The 72-hour rule</strong> — half enforced, and the half that is enforced is
              the half the recorded facts can support. Scheduling refuses a window whose start is
              nearer than the notice period it claims, so a window recorded as “72 hours before”
              really did have 72 hours ahead of it. What is <em>not</em> enforced is the rule&rsquo;s
              other half — that a full-downtime release must pick 72 — because nothing marks a
              window as full downtime: a Target of Platform covers both the 4.9 release and Points
              Mall fulfilment, so demanding 72 hours of every platform window would be a false
              alarm on half of them. The stored lead is shown unjudged on existing rows.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            {/* `actual_start` and `actual_end` are both stored and are the two
                facts an uptime figure would be computed from; the reports module
                simply has no definition that reads them yet. */}
            <div>
              <strong>Uptime report</strong> — the times a window really started and really
              finished are recorded, and History shows them beside what was planned. Reports has
              no uptime view or monthly total yet, so nothing adds them up for you.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              <strong>The “Planned” chip</strong> — there is none. What is actually stored
              is{' '}
              {loadFailed
                ? 'statuses this screen never saw, because the read failed and no window came back'
                : `${storedStatuses.length > 0 ? 'only ' : ''}${
                    storedStatuses.map((s) => statusWords(s).label).join(' and ') || 'nothing yet'
                  } in this scope`}
              , and nothing ever marks a window as planned, so a provisional window reads as
              Scheduled.
            </div>
          </div>
        </div>
      </Panel>

      {/* ── LISTS.maintHistory ─────────────────────────────────────────────── */}
      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title="Maintenance history"
        wide
        actions={<Button onClick={() => setHistoryOpen(false)}>Close</Button>}
      >
        <div className="notice notice-quiet" style={{ marginBottom: 'var(--space-4)' }}>
          <Icon name="info" />
          <div>Actual start and end times feed the uptime report.</div>
        </div>
        <DataTable
          caption="Completed maintenance windows, comparing planned times with actual times"
          columns={historyColumns}
          rows={history}
          rowKey={(w) => w.id}
          empty={
            loadFailed ? (
              <EmptyState
                icon="clock-counter-clockwise"
                title="History could not be read"
              >
                History is the same read as the table behind this dialog, and that read failed.
                Nothing came back to narrow, so this is not a claim that no window has run.
              </EmptyState>
            ) : (
              <EmptyState icon="clock-counter-clockwise" title="No window has run yet">
                No window in this scope carries an actual start or end. This is a real zero —
                history is the same live read as the table behind this dialog, narrowed to windows
                with recorded actual times.
              </EmptyState>
            )
          }
        />
      </Dialog>

      {/* ── Row → Open. A read-only detail view, not an editor. ─────────────── */}
      {detail && (
        <Drawer
          open
          onClose={() => setDetail(null)}
          title={detail.ref}
          subtitle={`${detail.title} · ${TARGET_LABEL[detail.target] ?? detail.target}`}
          actions={
            <>
              <Button onClick={() => setDetail(null)}>Close</Button>
              {isCancellable(detail) ? (
                <Button
                  variant="primary"
                  onClick={() => {
                    setCancelReason(CANCEL_REASONS[0].code);
                    setCancelNote('');
                    setCancelling(detail);
                  }}
                >
                  Cancel window
                </Button>
              ) : null}
            </>
          }
        >
          <div className="notice notice-quiet" id="maintenance-window-gap">
            <Icon name={isCancellable(detail) ? 'info' : 'lock-simple'} />
            <div>
              {detail.status === 'CANCELLED' ? (
                <>
                  {/* The window itself has no `cancellation_reason` column — the
                      reason travels with the audit entry and nowhere else — so
                      the operator is told where to go and look rather than which
                      column is missing. */}
                  <strong>This window is already cancelled.</strong> Cancelling is given once, and
                  its slot on{' '}
                  {TARGET_LABEL[detail.target] ?? detail.target.toLowerCase()} is free again — a
                  cancelled window is excluded from the overlap rule. The reason it was called off
                  is kept on the audit trail and not on the window, so look there to find out why.
                </>
              ) : detail.status === 'COMPLETED' ? (
                <>
                  <strong>This window has run and completed.</strong> A window that happened cannot
                  be called off, and its actual times below are what the uptime report is about.
                </>
              ) : hasStarted(detail) ? (
                <>
                  <strong>This window has already started, so it is immutable</strong> — players are
                  looking at the maintenance screen now, and withdrawing the window would not
                  withdraw what they were shown. Its timing, scope and notification plan can no
                  longer be changed by anyone.
                </>
              ) : (
                <>
                  The window stays cancellable until it starts. Cancelling records a reason on the
                  audit trail and frees its slot on the target. {WRITE_SCOPE}
                </>
              )}
            </div>
          </div>

          <div className="panel-title">Window</div>
          <dl className="stack" style={{ gap: 'var(--space-2)' }}>
            <div className="def-row">
              <dt>Reference</dt>
              <dd>{detail.ref}</dd>
            </div>
            <div className="def-row">
              <dt>Affected surface</dt>
              <dd>
                {TARGET_LABEL[detail.target] ?? detail.target}
                {detail.target_id ? ` · ${detail.target_id}` : ''}
              </dd>
            </div>
            <div className="def-row">
              <dt>Brand</dt>
              <dd>{detail.brand ?? 'All brands'}</dd>
            </div>
            <div className="def-row">
              <dt>Status</dt>
              <dd>{statusWords(shownStatus(detail)).label}</dd>
            </div>
            <div className="def-row">
              <dt>Notice given</dt>
              <dd>{leadLabel(detail.notice_lead_minutes)}</dd>
            </div>
            <div className="def-row">
              <dt>Created by</dt>
              <dd>{detail.created_by ?? 'unattributed'}</dd>
            </div>
          </dl>

          <div className="panel-title">Planned</div>
          <dl className="stack" style={{ gap: 'var(--space-2)' }}>
            <div className="def-row">
              <dt>Start</dt>
              <dd>{stamp(detail.planned_start)}</dd>
            </div>
            <div className="def-row">
              <dt>End</dt>
              <dd>{stamp(detail.planned_end)}</dd>
            </div>
            <div className="def-row">
              <dt>Duration</dt>
              <dd>{durationLabel(detail.planned_start, detail.planned_end)}</dd>
            </div>
          </dl>

          <div className="panel-title">Actual</div>
          <dl className="stack" style={{ gap: 'var(--space-2)' }}>
            <div className="def-row">
              <dt>Start</dt>
              <dd>{stamp(detail.actual_start)}</dd>
            </div>
            <div className="def-row">
              <dt>End</dt>
              <dd>{stamp(detail.actual_end)}</dd>
            </div>
            <div className="def-row">
              {/* Kept against the plan rather than as a bare duration: the uptime
                  report is about the gap between the two, not either alone. */}
              <dt>Against plan</dt>
              <dd>
                {detail.actual_start === null && detail.actual_end === null ? (
                  <span className="text-subtle">not started</span>
                ) : (
                  resultLabel(detail)
                )}
              </dd>
            </div>
          </dl>

          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              Player impact copy and the notification channel mix are absent because the platform
              keeps neither. What players were told, and through which channel, cannot be
              reconstructed from this window.
            </div>
          </div>
        </Drawer>
      )}

      {/* ── FORMS.maint — scheduling a window ──────────────────────────────── */}
      <Dialog
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        title="Schedule maintenance window"
        wide
        actions={
          <>
            <Button onClick={() => setScheduleOpen(false)}>Cancel</Button>
            <Button variant="primary" disabled={saving} onClick={() => void submitSchedule()}>
              {saving ? 'Scheduling…' : 'Schedule window'}
            </Button>
          </>
        }
      >
        <div className="stack">
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              Full-downtime releases need 72 hours notice; partial windows need 24. The notice
              period has to fit before the start, and no window may overlap another on the same
              target. Both are refused at save. Nothing is sent to players: the lead is recorded,
              the message is not.
            </div>
          </div>

          <Field
            label="Title shown on the calendar"
            hint="The surface as a player would recognise it — “Evolution Live casino”, not a category."
            error={formErrors['title']}
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Evolution Live casino"
              />
            )}
          </Field>

          <div className="halves">
            <Field
              label="Target"
              hint="The category the overlap rule is scoped by, not the name."
            >
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={form.target}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      target: e.target.value as (typeof TARGET_OPTIONS)[number]['value'],
                    }))
                  }
                >
                  {TARGET_OPTIONS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field
              label="Target identifier"
              hint="Optional. Left blank, the whole category counts as one surface for the overlap rule — two provider windows would then clash even on different providers."
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={form.targetId}
                  onChange={(e) => setForm((f) => ({ ...f, targetId: e.target.value }))}
                  placeholder="evolution"
                />
              )}
            </Field>
          </div>

          <Field
            label="Brand"
            hint="Leave as all brands for a platform-wide window; it then appears in every brand scope."
            error={formErrors['brandId']}
          >
            {({ id, describedBy, invalid }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                value={form.brandId}
                onChange={(e) => setForm((f) => ({ ...f, brandId: e.target.value }))}
              >
                <option value="">All brands</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <div className="halves">
            <Field label="Start (PHT)" error={formErrors['start']}>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  type="datetime-local"
                  aria-describedby={describedBy}
                  aria-invalid={invalid || undefined}
                  value={form.start}
                  onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))}
                />
              )}
            </Field>
            <Field label="End (PHT)" error={formErrors['end']}>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  type="datetime-local"
                  aria-describedby={describedBy}
                  aria-invalid={invalid || undefined}
                  value={form.end}
                  onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))}
                />
              )}
            </Field>
          </div>

          <Field
            label="Notice to players"
            hint="Kept with the window as the notice period. It records when players are due to be told, and nothing here tells them."
            error={formErrors['lead']}
          >
            {({ id, describedBy, invalid }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                value={String(form.lead)}
                onChange={(e) => setForm((f) => ({ ...f, lead: Number(e.target.value) }))}
              >
                {LEAD_OPTIONS.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <div className="notice notice-quiet">
            <Icon name="translate" />
            <div>
              Times are read and written in Philippine time, the clock the player-facing maintenance
              screen counts down in — not this browser's. Asia/Manila has kept no daylight saving
              since 1978, so the offset is a fixed +08:00.
            </div>
          </div>
        </div>
      </Dialog>

      {/* ── Calling a planned window off ───────────────────────────────────── */}
      <Dialog
        open={cancelling !== null}
        onClose={() => setCancelling(null)}
        title={cancelling ? `Cancel ${cancelling.ref}?` : 'Cancel window'}
        /*
         * NOT `dismissible={!saving}`, which is the shape this was nearly
         * written in. That guard blocks Escape only WHILE THE WRITE IS IN
         * FLIGHT — the one moment the decision has already been made and the
         * keystroke can no longer do any harm — and leaves the dialog freely
         * dismissible before the press, which is the moment that matters: a
         * stray Escape there silently abandons a cancellation somebody opened
         * this drawer to make, with nothing on screen to say it was dropped.
         * The guard belongs on the other side of the button, so it is simply
         * false: the two ways out are Keep the window and Cancel the window,
         * and both of them are an answer.
         */
        dismissible={false}
        actions={
          <>
            <Button onClick={() => setCancelling(null)}>Keep the window</Button>
            <Button
              variant="primary"
              disabled={cancelNote.trim().length < 10 || saving}
              onClick={() => void submitCancel()}
            >
              {saving ? 'Cancelling…' : 'Cancel the window'}
            </Button>
          </>
        }
      >
        <div className="stack">
          {/* ── WARN AND NOT DANGER, AND THE CHOICE WAS READ OFF THE ROUTE ────
              The tone contract beside `.notice-warn` in the design system gives
              warn a reversible consequence and keeps danger for the irreversible
              and for anything that moves money or a reported figure. Cancelling
              a window is given once — the platform refuses a second cancellation
              and no route un-cancels one — but the CONSEQUENCE is reversible in
              the only sense an operator cares about: the work can be booked
              again, and freeing the slot is precisely what makes that possible,
              because the overlap rule skips cancelled windows.

              No money moves, and no reported figure moves either. The uptime
              report is computed from the ACTUAL times of windows that ran, and
              this route refuses once `planned_start` has passed, so a window
              called off here has no actual times and never reached the report.

              If this wore the same red as refusing a player's withdrawal, the
              red would stop meaning anything.

              ── WHAT THE NOTICE HAD TO ADD ──────────────────────────────────
              The first sentence was already here as the muted paragraph under
              the title. It has been MOVED, not copied — same wording — because
              it is exactly what the notice exists to say, and two copies of one
              sentence are one copy too many to keep true.

              The rest is what an operator cannot see from the title. They chose
              a notice lead when they booked the window, so the natural
              assumption is that calling it off tells somebody; nothing does.
              `platform.maintenance_window` has no link to `platform.announcement`
              and nothing player-facing reads it, so the cancellation reaches
              exactly as far as this console. Schedule window is named because it
              is the real way back AND it is on this screen: the primary button
              in the calendar header, beside History, labelled exactly that. Rename
              it and this sentence becomes a dead end, which is the one way advice
              like this goes wrong. */}
          <div className="notice notice-warn">
            <Icon name="warning" />
            <div>
              {cancelling
                ? `${cancelling.ref} — ${cancelling.title}, ${whenLabel(cancelling)} — is dropped from the calendar and its slot on ${TARGET_LABEL[cancelling.target] ?? cancelling.target} is free for another window.`
                : ''}{' '}
              <strong>Cancelling is given once</strong> and nothing puts this window back, so if
              the work is still going to happen it has to be booked again with Schedule window.
              Nobody is told it was called off — not players, not partners — whatever notice the
              window promised when it was booked.
            </div>
          </div>

          <div className="notice notice-quiet">
            <Icon name="info" />
            {/* `platform.maintenance_window` has no `cancellation_reason` column,
                so the audit entry is the only place the reason exists. Both are
                written in one transaction, which is the half of this that an
                operator about to type a reason actually needs: the reason cannot
                be lost while the cancellation succeeds, and it cannot be found on
                the window afterwards. */}
            <div>
              The reason you give is saved on the audit trail together with the cancellation, in
              one step — either both are recorded or neither is. It is not kept on the window, so
              the audit trail is the only record of why this was called off.
            </div>
          </div>

          <Field label="Reason" hint="A closed set, so refusals can be counted by cause.">
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              >
                {CANCEL_REASONS.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Note"
            hint="At least 10 characters. A code on its own reads as a form number; this is what the next person to ask will actually read."
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={cancelNote}
                onChange={(e) => setCancelNote(e.target.value)}
                placeholder="Vendor moved the upgrade to the following week."
              />
            )}
          </Field>
        </div>
      </Dialog>
    </>
  );
}
